import { useEffect } from 'react';
import { SeoConfig, siteMetadata } from './seoConfig';

const upsertMeta = (selector: string, attributes: Record<string, string>): HTMLMetaElement => {
  let element = document.head.querySelector<HTMLMetaElement>(selector);
  if (!element) {
    element = document.createElement('meta');
    document.head.appendChild(element);
  }
  Object.entries(attributes).forEach(([name, value]) => element?.setAttribute(name, value));
  return element;
};

const upsertCanonical = (href?: string): void => {
  const existing = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!href) {
    existing?.remove();
    return;
  }
  const canonical = existing ?? document.createElement('link');
  canonical.rel = 'canonical';
  canonical.href = href;
  if (!existing) document.head.appendChild(canonical);
};

const buildStructuredData = (config: SeoConfig): unknown[] => {
  const organization = {
    '@type': 'Organization',
    '@id': `${siteMetadata.siteUrl}/#organization`,
    name: siteMetadata.siteName,
    url: `${siteMetadata.siteUrl}/`,
    logo: `${siteMetadata.siteUrl}${siteMetadata.logo}`,
    /* Ties the brand name to its profiles elsewhere, so answer engines can
       tell IMABIRD the company from the word. */
    ...(siteMetadata.sameAs.length ? { sameAs: siteMetadata.sameAs } : {}),
  };

  const page = {
    '@type': 'WebPage',
    '@id': `${config.canonicalUrl ?? `${siteMetadata.siteUrl}/`}#webpage`,
    url: config.canonicalUrl,
    name: config.title,
    description: config.description,
    isPartOf: { '@id': `${siteMetadata.siteUrl}/#website` },
    about: { '@id': `${siteMetadata.siteUrl}/#organization` },
  };

  const graph: unknown[] = [
    organization,
    {
      '@type': 'WebSite',
      '@id': `${siteMetadata.siteUrl}/#website`,
      url: `${siteMetadata.siteUrl}/`,
      name: siteMetadata.siteName,
      publisher: { '@id': `${siteMetadata.siteUrl}/#organization` },
      inLanguage: 'en',
    },
    page,
  ];

  if (config.path === '/') {
    graph.push({
      '@type': 'SoftwareApplication',
      name: siteMetadata.siteName,
      applicationCategory: 'MultimediaApplication',
      operatingSystem: 'Web',
      url: config.canonicalUrl,
      description: config.description,
      offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
    });
  }

  /* Breadcrumbs still render in search results, unlike FAQ and HowTo markup,
     so every indexable page below the root declares where it sits. */
  if (config.indexable && config.canonicalUrl && config.path !== '/') {
    graph.push({
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${siteMetadata.siteUrl}/` },
        { '@type': 'ListItem', position: 2, name: config.title.split(' | ')[0].split(': ')[0], item: config.canonicalUrl },
      ],
    });
  }

  if (config.faq?.length) {
    graph.push({
      '@type': 'FAQPage',
      '@id': `${config.canonicalUrl ?? siteMetadata.siteUrl}#faq`,
      mainEntity: config.faq.map((entry) => ({
        '@type': 'Question',
        name: entry.question,
        acceptedAnswer: { '@type': 'Answer', text: entry.answer },
      })),
    });
  }

  if (config.howTo?.steps.length) {
    graph.push({
      '@type': 'HowTo',
      '@id': `${config.canonicalUrl ?? siteMetadata.siteUrl}#howto`,
      name: config.howTo.name,
      step: config.howTo.steps.map((step, index) => ({
        '@type': 'HowToStep',
        position: index + 1,
        name: step.title,
        text: step.body,
      })),
    });
  }

  if (config.article) {
    graph.push({
      '@type': 'BlogPosting',
      headline: config.article.headline,
      description: config.description,
      datePublished: config.article.publishedAt,
      dateModified: config.article.modifiedAt,
      mainEntityOfPage: config.canonicalUrl,
      image: config.imageUrl,
      author: { '@id': `${siteMetadata.siteUrl}/#organization` },
      publisher: { '@id': `${siteMetadata.siteUrl}/#organization` },
    });
  }

  return graph;
};

const Seo: React.FC<{ config: SeoConfig }> = ({ config }) => {
  useEffect(() => {
    document.title = config.title;

    upsertMeta('meta[name="description"]', { name: 'description', content: config.description });
    upsertMeta('meta[name="robots"]', {
      name: 'robots',
      content: config.indexable
        ? 'index, follow, max-image-preview:large'
        : 'noindex, nofollow',
    });
    upsertMeta('meta[name="googlebot"]', {
      name: 'googlebot',
      content: config.indexable
        ? 'index, follow, max-image-preview:large'
        : 'noindex, nofollow',
    });

    upsertCanonical(config.indexable ? config.canonicalUrl : undefined);

    const socialTags: Array<[string, Record<string, string>]> = [
      ['meta[property="og:type"]', { property: 'og:type', content: config.type }],
      ['meta[property="og:site_name"]', { property: 'og:site_name', content: siteMetadata.siteName }],
      ['meta[property="og:title"]', { property: 'og:title', content: config.title }],
      ['meta[property="og:description"]', { property: 'og:description', content: config.description }],
      ['meta[property="og:image"]', { property: 'og:image', content: config.imageUrl }],
      ['meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary_large_image' }],
      ['meta[name="twitter:title"]', { name: 'twitter:title', content: config.title }],
      ['meta[name="twitter:description"]', { name: 'twitter:description', content: config.description }],
      ['meta[name="twitter:image"]', { name: 'twitter:image', content: config.imageUrl }],
    ];
    if (config.canonicalUrl) {
      socialTags.push(['meta[property="og:url"]', { property: 'og:url', content: config.canonicalUrl }]);
    } else {
      document.head.querySelector('meta[property="og:url"]')?.remove();
    }
    socialTags.forEach(([selector, attributes]) => upsertMeta(selector, attributes));

    let structuredData = document.head.querySelector<HTMLScriptElement>('script[data-seo-structured-data]');
    if (!structuredData) {
      structuredData = document.createElement('script');
      structuredData.type = 'application/ld+json';
      structuredData.dataset.seoStructuredData = 'true';
      document.head.appendChild(structuredData);
    }
    structuredData.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@graph': buildStructuredData(config),
    });
  }, [config]);

  return null;
};

export default Seo;
